import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { SchrijvenScore, SchrijvenTaskType, SchrijvenVariantContext } from "@a2/core";
import { SCHRIJVEN_TASK_LABELS, bandFor, BAND_LABEL, totalScore, SCHRIJVEN_RUBRIC } from "@a2/core";
import { authedFetch } from "../../lib/api-client";
import { SchrijvenFeedbackBubble } from "../../components/schrijven/SchrijvenFeedbackBubble";
import { useT } from "../../lib/use-t";

type TurnView = {
  id: string;
  taskType: SchrijvenTaskType;
  userText: string;
  variant: SchrijvenVariantContext | null;
  score: SchrijvenScore | null;
  tutorParagraph: string | null;
};

type SessionData = {
  session: { id: string; startedAt: string };
  turns: Array<{
    id: string;
    taskType: string | null;
    userText: string;
    variant: SchrijvenVariantContext | null;
    score: unknown;
    feedbackMd: string;
  }>;
};

function isSchrijvenScore(v: unknown): v is SchrijvenScore {
  if (!v || typeof v !== "object") return false;
  const rec = v as Record<string, unknown>;
  const keys: Array<keyof SchrijvenScore> = ["execution", "grammar", "spelling", "clearness", "vocabulary"];
  return keys.every((k) => typeof rec[k] === "number");
}

function toTurnView(t: SessionData["turns"][number]): TurnView | null {
  if (!t.taskType) return null;
  return {
    id: t.id,
    taskType: t.taskType as SchrijvenTaskType,
    userText: t.userText,
    variant: t.variant,
    score: isSchrijvenScore(t.score) ? t.score : null,
    tutorParagraph: t.feedbackMd || null,
  };
}

const MAX = SCHRIJVEN_RUBRIC.reduce((s, c) => s + c.max, 0);

function OverallVerdict({ turns }: { turns: TurnView[] }) {
  const t = useT();
  const submitted = turns.filter((turn) => turn.score !== null);
  if (submitted.length === 0) return null;
  const totals = submitted.map((turn) => totalScore(turn.score!));
  const grandTotal = totals.reduce((a, b) => a + b, 0);
  const grandMax = MAX * submitted.length;
  const pct = grandTotal / grandMax;
  const passAll = submitted.every((turn) => bandFor(turn.score!) === "pass");
  const overallBand = passAll ? "pass" : pct >= 0.5 ? "warn" : "fail";
  const bandColor =
    overallBand === "pass"
      ? "var(--pass)"
      : overallBand === "warn"
        ? "var(--warn)"
        : "var(--fail)";

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 space-y-2">
      <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
        {t("schrijven.report.totalLabel")}
      </span>
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-semibold tracking-tight" style={{ color: bandColor }}>
          {grandTotal}/{grandMax}
        </span>
        <span className="font-serif-italic text-[var(--ink-muted)] text-lg">
          {BAND_LABEL[overallBand]}
        </span>
      </div>
    </div>
  );
}

export function SchrijvenReportPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const t = useT();
  const [data, setData] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    authedFetch(`/schrijven/sessions/${sessionId}`)
      .then((r) => r.json())
      .then((body: { ok: boolean; data?: SessionData; error?: { message: string } }) => {
        if (!body.ok) {
          setError(body.error?.message ?? "Niet gevonden");
          return;
        }
        setData(body.data ?? null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Fout"));
  }, [sessionId]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
        <p className="text-[var(--fail)] text-sm">{error}</p>
        <a href="/schrijven" className="text-sm text-[var(--ink-muted)] underline">Terug</a>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-[var(--ink-faint)] text-sm">{t("schrijven.history.loading")}</p>
      </div>
    );
  }

  const turns = data.turns.map(toTurnView).filter((t): t is TurnView => t !== null);

  return (
    <main className="mx-auto max-w-3xl px-5 sm:px-6 py-8 sm:py-12 space-y-8">
      <div className="space-y-1">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("schrijven.report.eyebrow")}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--ink)]">
          {t("schrijven.report.title")}
        </h1>
      </div>

      <OverallVerdict turns={turns} />

      <div className="space-y-8">
        {turns.map((turn) => (
          <section key={turn.id} className="space-y-4">
            <h2 className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
              {SCHRIJVEN_TASK_LABELS[turn.taskType]}
            </h2>

            {turn.variant ? (
              <div className="rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-4 py-3 text-sm text-[var(--ink-muted)]">
                {turn.variant.scenario}
              </div>
            ) : null}

            {turn.userText.trim() ? (
              <div className="rounded-lg border border-[var(--border)] bg-[var(--panel)] px-4 py-3 text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">
                {turn.userText}
              </div>
            ) : (
              <p className="text-sm text-[var(--ink-faint)] italic">{t("schrijven.report.notSubmitted")}</p>
            )}

            {turn.score && turn.tutorParagraph ? (
              <SchrijvenFeedbackBubble
                score={turn.score}
                tutorParagraph={turn.tutorParagraph}
                bullets={
                  turn.variant?.bullets.map((b) => ({ text: b, covered: false })) ?? []
                }
              />
            ) : null}
          </section>
        ))}
      </div>

      <div className="pt-4 border-t border-[var(--border)]">
        <a
          href="/schrijven"
          className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
        >
          ← {t("schrijven.report.backToWriting")}
        </a>
      </div>
    </main>
  );
}
